import { EntityEnvironment, bridge, descriptors } from "@dinahmoe/dmafjs-core";

import { visualEntities } from "../entities.js";
import { visualNodes } from "../nodes.js";
import { addChild, moveChild } from "../helpers/elements.helper.js";
import {
  addPropertyValue,
  addToPendingLayout,
  cssValue,
  removePropertyValue,
} from "../helpers/styles.helper.js";
import { normalizeTouchEvent } from "../helpers/event.helper.js";
import { domEnvironment } from "../utils/dom-environment.util.js";
import { focusManager } from "../utils/focus.util.js";
import { visualTransformEngine } from "../utils/transform-engine.util.js";

const COLOR_CSS_MAP = {
  "text.color": "color",
  "appearance.background": "backgroundColor",
};

function isDynamicSize(value) {
  if (typeof value === "string") return value.includes("%");
  return false;
}

const pendingInitialResize = [];

function flushInitialResizes() {
  for (const eNode of pendingInitialResize) {
    visualSuperHtml.doResize(eNode);
  }
  pendingInitialResize.length = 0;
}


class VisualSuperHtml extends EntityEnvironment {
  constructor(name) {
    super(name);
    if (name) {
      visualEntities.set(name, this);
    }
  }

  handlers = this.extendParentHandlers({
    "size.width":             this.addNodeToPendingLayout,
    "size.height":            this.addNodeToPendingLayout,
    "size.widthAuto":         this.setWidthAuto,
    "size.heightAuto":        this.setHeightAuto,
    "anchor.x":               this.addNodeToPendingLayout,
    "anchor.y":               this.addNodeToPendingLayout,
    "position.x":             this.setTranslate,
    "position.y":             this.setTranslate,
    "position.z":             this.setTranslate,
    "transform.scale":        this.setScale,
    "transform.scaleX":       this.setScale,
    "transform.scaleY":       this.setScale,
    "transform.rotate":       this.setRotate,
    "transform.rotateX":      this.setResidualTransform,
    "transform.rotateY":      this.setResidualTransform,
    "transform.rotateZ":      this.setResidualTransform,
    "transform.perspective":  this.setResidualTransform,
    "transform.originX":      this.setOriginPoint,
    "transform.originY":      this.setOriginPoint,
    "appearance.opacity":     this.setOpacity,
    "appearance.blur":        this.setFilter,
    "shadow.x":               this.setFilter,
    "shadow.y":               this.setFilter,
    "shadow.blur":            this.setFilter,
    "shadow.color":           this.setFilter,
    "border.width":           this.setBorderWidth,
    "transform":              this.handleTransform,
    "events":                 this.setUserEvents,
    "parentUid":              this.setParentUid,
    "className":              this.setClassName,
  });

  extendParentHandlers(handlers) {
    return {
      ...this.handlers,
      ...handlers,
    };
  }

  create(eNode) {
    for (const key of Object.keys(eNode.patch)) {
      eNode.props[key] = eNode.patch[key];
    }

    this.setProps(eNode);
  }

  setProps(eNode) {
    for (const key of Object.keys(eNode.props)) {
      const entry = eNode.envImplementation.handlers[key];

      if (entry) {
        const handler = typeof entry === "function" ? entry : entry.handler;
        handler.call(eNode.envImplementation, eNode);
      }

      if (typeof eNode.envImplementation[key] === "function") {
        eNode.envImplementation[key](eNode);
      } else if (!entry) {
        const derivedName = key.replace(/\.(\w)/g, (_, c) => c.toUpperCase());
        if (typeof eNode.envImplementation[derivedName] === "function") {
          eNode.envImplementation[derivedName](eNode);
        } else if (key.includes(".")) {
          console.warn(`[visual] no handler or convention method for "${key}" (tried "${derivedName}") on ${eNode.uid}`);
        }
      }
    }

    addChild(eNode);

    const hasDynamicSize = eNode.props["size.widthAuto"] || eNode.props["size.heightAuto"] || isDynamicSize(eNode.props["size.height"]) || isDynamicSize(eNode.props["size.width"]);
    if (hasDynamicSize) {
      this.setDynamicSize(eNode);
    }

    // Hash navigation: elements with hashId need a resize observer to report their
    // absoluteY position to core for deeplinking (scrolling to hash on page load).
    // Only set up if not already created by setDynamicSize.
    if (eNode.props["hashId"] !== undefined && !hasDynamicSize) {
      const resizeObserver = new ResizeObserver(this.doResize.bind(this, eNode));
      resizeObserver.observe(eNode.element);
    }
  }

  setDynamicSize(eNode) {
    const h = eNode.props["size.height"];
    if (eNode.props["size.heightAuto"]) {
      eNode.hasDynamicHeight = true;
      eNode.element.style.height = "fit-content";
      eNode.scrollHeight = -1;
    } else if (isDynamicSize(h)) {
      eNode.hasDynamicHeight = true;
      eNode.scrollHeight = -1;
    }

    const w = eNode.props["size.width"];
    if (eNode.props["size.widthAuto"]) {
      eNode.hasDynamicWidth = true;
      eNode.element.style.width = "fit-content";
      eNode.scrollWidth = -1;
    } else if (isDynamicSize(w)) {
      eNode.hasDynamicWidth = true;
      eNode.scrollWidth = -1;
    }

    this._ensureResizeObserver(eNode);
  }

  doResize(eNode) {
    const element = eNode.element;

    // solves a feedback loop
    if (element.scrollHeight === eNode.scrollHeight && element.scrollWidth === eNode.scrollWidth) {
      return;
    }

    eNode.scrollWidth = element.scrollWidth;
    eNode.scrollHeight = element.scrollHeight;

    bridge.update({
      uid: eNode.uid,
      key: "resize",
      value: {
        id: eNode.props["id"],
        hashId: eNode.props["hashId"],
        scrollWidth: eNode.scrollWidth,
        scrollHeight: eNode.scrollHeight,
        absoluteY: element.offsetTop,
      },
      sender: "browser",
    });
  }

  setWidthAuto(eNode) {
    if (eNode.props["size.widthAuto"]) {
      eNode.hasDynamicWidth = true;
      eNode.element.style.width = "fit-content";
      eNode.scrollWidth = -1;
      this._ensureResizeObserver(eNode);
    } else {
      eNode.hasDynamicWidth = false;
      this.applyCssWidth(eNode);
      this._cleanupResizeObserver(eNode);
    }
  }

  setHeightAuto(eNode) {
    if (eNode.props["size.heightAuto"]) {
      eNode.hasDynamicHeight = true;
      eNode.element.style.height = "fit-content";
      eNode.scrollHeight = -1;
      this._ensureResizeObserver(eNode);
    } else {
      eNode.hasDynamicHeight = false;
      this.applyCssHeight(eNode);
      this._cleanupResizeObserver(eNode);
    }
  }

  _ensureResizeObserver(eNode) {
    if (eNode._resizeObserver) return;
    const resizeObserver = new ResizeObserver(this.doResize.bind(this, eNode));
    resizeObserver.observe(eNode.element);
    eNode._resizeObserver = resizeObserver;
    pendingInitialResize.push(eNode);
  }

  _cleanupResizeObserver(eNode) {
    if (!eNode._resizeObserver) return;
    if (eNode.hasDynamicWidth || eNode.hasDynamicHeight) return;
    eNode._resizeObserver.disconnect();
    eNode._resizeObserver = null;
  }

  addNodeToPendingLayout(eNode) {
    addToPendingLayout(eNode);
  }

  setUserEvents(eNode) {
    if (!eNode.props.events || eNode.props.events.length === 0) return;

    eNode._eventTypes = new Set();
    eNode._stopPropagation = {};

    for (const entry of eNode.props.events) {
      eNode._eventTypes.add(entry.type);
      eNode._stopPropagation[entry.type] = entry.stopPropagation !== false;

      if (entry.routeId && entry.navigationUid) {
        const navENode = visualNodes.get(entry.navigationUid);
        const route = navENode?.props.routes?.find(r => r.id === entry.routeId);
        if (route) {
          eNode.element.setAttribute('data-href', route.path);
          if (route.target) eNode.element.setAttribute('data-target', route.target);
          if (route.external) eNode.element.setAttribute('data-external', 'true');
        }
      }
    }

    for (const eventType of eNode._eventTypes) {
      switch (eventType) {
        case "gesture":
          this._setupGestureEvent(eNode);
          break;
        case "down":
          eNode.element.addEventListener("pointerdown", this._dispatchMessage.bind(this, eNode, "down"));
          break;
        case "up":
          eNode.element.addEventListener("pointerup", this._dispatchMessage.bind(this, eNode, "up"));
          break;
        case "click":
          eNode.element.addEventListener("click", this._dispatchMessage.bind(this, eNode, "click"));
          break;
        case "double_click":
          eNode.element.addEventListener("dblclick", this._dispatchMessage.bind(this, eNode, "double_click"));
          break;
        case "over":
          eNode.element.addEventListener("pointerover", this._dispatchMessage.bind(this, eNode, "over"));
          break;
        case "out":
          eNode.element.addEventListener("pointerout", this._dispatchMessage.bind(this, eNode, "out"));
          break;
        case "scroll":
          eNode.element.addEventListener("scroll", this._dispatchScrollEvent.bind(this, eNode, "scroll"));
          eNode.element.addEventListener("wheel", (event) => {
            if (eNode._stopPropagation?.["scroll"] ?? true) event.stopPropagation();
          });
          break;
        case "scroll-smooth":
          this._setupSmoothScroll(eNode, eventType);
          break;
        case "move":
          eNode.element.addEventListener("pointermove", this._dispatchMessage.bind(this, eNode, "move"));
          break;
        case "drag":
          this._setupDragEvent(eNode);
          break;
        case "drop":
          this._setupDropEvent(eNode);
          break;
        case "focus":
          focusManager.addFocusableNode(eNode);
          break;
        case "intersection":
          this._observeIntersection(eNode);
          break;
        default:
          console.error(`event type ${eventType} is not implemented`);
      }
    }

    eNode.element.style.pointerEvents = "auto";
    this._setElementCursor(eNode);
  }

  setParentUid(eNode) {
    // TODO look at, probably broken
    if (!eNode.element.parentElement) {
      return;
    }
    moveChild(eNode);
  }

  setClassName(eNode) {
    // TODO remove this
    if (eNode.element instanceof SVGElement) {
      eNode.element.className.baseVal = eNode.props["className"];
      return;
    }
    eNode.element.className = eNode.props["className"];
  }

  applyCssWidth(eNode) {
    const w = eNode.props["size.width"];
    if (typeof w === "string" && w.includes("%")) {
      eNode.element.style.width = w.endsWith("%") ? w : `calc(${w})`;
    } else if (w >= 0 && !eNode.hasDynamicWidth) {
      eNode.element.style.width = `${w}px`;
    }
  }

  applyCssHeight(eNode) {
    const h = eNode.props["size.height"];
    if (typeof h === "string" && h.includes("%")) {
      eNode.element.style.height = h.endsWith("%") ? h : `calc(${h})`;
    } else if (h >= 0 && !eNode.hasDynamicHeight) {
      eNode.element.style.height = `${h}px`;
    }
  }

  applyLayout(eNode) {
    var parent = visualNodes.get(eNode.props.parentUid);
    if (!parent) return;

    const style = eNode.element.style;

    // Flex child: set position and dimensions, let flexbox handle layout
    if (parent.hasFlex && eNode.props["position.mode"] !== "absolute") {
      style.position = "relative";
      if (eNode.props["flex.shrink"] === undefined) {
        style.flexShrink = "0";
      }
      this.applyCssWidth(eNode);
      this.applyCssHeight(eNode);
      return;
    }

    // Store anchor types for setTranslate
    eNode.anchorXType = eNode.props["anchor.x"] || 'left';
    eNode.anchorYType = eNode.props["anchor.y"] || 'top';

    if (eNode.props["position.mode"] === "absolute") {
      style.position = "absolute";
      switch (eNode.anchorXType) {
        case "left":   style.left = "0px"; break;
        case "center": style.left = "50%"; break;
        case "right":  style.right = "0px"; break;
      }
      switch (eNode.anchorYType) {
        case "top":    style.top = "0px"; break;
        case "center": style.top = "50%"; break;
        case "bottom": style.bottom = "0px"; break;
      }
    } else {
      switch (eNode.anchorXType) {
        case "left":   style.left = "0px"; break;
        case "center": style.left = "50%"; break;
        case "right":  style.left = "100%"; break;
      }
      switch (eNode.anchorYType) {
        case "top":    style.top = "0px"; break;
        case "center": style.top = "50%"; break;
        case "bottom": style.top = "100%"; break;
      }
    }

    this.applyCssWidth(eNode);
    this.applyCssHeight(eNode);
  }

  applyTransforms(eNode) {
    this.setTranslate(eNode);
    this.setScale(eNode);
    this.setRotate(eNode);
    this.setResidualTransform(eNode);
    this.setFilter(eNode);
  }

  setTranslate(eNode) {
    const style = eNode.element.style;
    const isAbsolute = eNode.props["position.mode"] === "absolute";
    const parent = visualNodes.get(eNode.props.parentUid);
    const isFlexChild = parent?.hasFlex && !isAbsolute;
    const z = eNode.props["position.z"];

    // Flex children don't use position offset (unless z is set for 3D)
    const hasPositionOffset = !isFlexChild || z !== undefined;
    const x = hasPositionOffset ? (eNode.props["position.x"] || 0) : 0;
    const y = hasPositionOffset ? (eNode.props["position.y"] || 0) : 0;

    // Determine anchor percentage for X
    let anchorXPct = null;
    if (isAbsolute) {
      if (eNode.anchorXType === 'center') anchorXPct = '-50%';
    } else {
      if (eNode.anchorXType === 'center') anchorXPct = '-50%';
      else if (eNode.anchorXType === 'right') anchorXPct = '-100%';
    }

    // Determine anchor percentage for Y
    let anchorYPct = null;
    if (isAbsolute) {
      if (eNode.anchorYType === 'center') anchorYPct = '-50%';
    } else {
      if (eNode.anchorYType === 'center') anchorYPct = '-50%';
      else if (eNode.anchorYType === 'bottom') anchorYPct = '-100%';
    }

    // Build translate X component — combine anchor % + position px via calc()
    let tx;
    if (anchorXPct && x !== 0) {
      tx = `calc(${anchorXPct} + ${x}px)`;
    } else if (anchorXPct) {
      tx = anchorXPct;
    } else {
      tx = `${x}px`;
    }

    // Build translate Y component
    let ty;
    if (anchorYPct && y !== 0) {
      ty = `calc(${anchorYPct} + ${y}px)`;
    } else if (anchorYPct) {
      ty = anchorYPct;
    } else {
      ty = `${y}px`;
    }

    if (z !== undefined) {
      style.translate = `${tx} ${ty} ${z}px`;
    } else {
      style.translate = `${tx} ${ty}`;
    }
  }

  setScale(eNode) {
    const style = eNode.element.style;
    const scale = eNode.props["transform.scale"];
    const scaleX = eNode.props["transform.scaleX"];
    const scaleY = eNode.props["transform.scaleY"];

    if (scale !== undefined) {
      style.scale = `${scale}`;
    } else if (scaleX !== undefined || scaleY !== undefined) {
      style.scale = `${scaleX ?? 1} ${scaleY ?? 1}`;
    }
  }

  setRotate(eNode) {
    const style = eNode.element.style;
    const rotate = eNode.props["transform.rotate"];
    if (rotate !== undefined) {
      style.rotate = `${rotate}deg`;
    }
  }

  setResidualTransform(eNode) {
    const style = eNode.element.style;
    const rotateX = eNode.props["transform.rotateX"];
    const rotateY = eNode.props["transform.rotateY"];
    const rotateZ = eNode.props["transform.rotateZ"];
    const perspective = eNode.props["transform.perspective"];

    const transforms = [];
    if (perspective !== undefined) transforms.push(`perspective(${perspective}px)`);
    if (rotateX !== undefined) transforms.push(`rotateX(${rotateX}deg)`);
    if (rotateY !== undefined) transforms.push(`rotateY(${rotateY}deg)`);
    if (rotateZ !== undefined) transforms.push(`rotateZ(${rotateZ}deg)`);

    style.transform = transforms.length ? transforms.join(' ') : '';
  }

  setFilter(eNode) {
    const style = eNode.element.style;
    let filterValue = style.filter || '';

    const blur = eNode.props["appearance.blur"];
    if (blur === undefined || blur === 0) {
      filterValue = removePropertyValue(filterValue, "blur");
    } else {
      filterValue = addPropertyValue(filterValue, "blur", `${blur}px`);
    }

    const shadowColor = eNode.props["shadow.color"];
    if (shadowColor === undefined) {
      filterValue = removePropertyValue(filterValue, "drop-shadow");
    } else {
      const dropShadowFilter = `${eNode.props["shadow.x"]}px ${eNode.props["shadow.y"]}px ${eNode.props["shadow.blur"]}px ${shadowColor}`;
      filterValue = addPropertyValue(filterValue, "drop-shadow", dropShadowFilter);
    }

    style.filter = filterValue;
    style.webkitFilter = filterValue;
  }

  setOriginPoint(eNode) {
    const oX = eNode.props["transform.originX"];
    const oY = eNode.props["transform.originY"];

    eNode.element.style.transformOrigin = `${oX !== undefined ? cssValue(oX) : "50%"} ${oY !== undefined ? cssValue(oY) : "50%"}`;
  }

  setOpacity(eNode) {
    const opacity = eNode.props["appearance.opacity"];
    eNode.element.style.opacity = opacity;

    if (eNode._transitioningOpacity) return;

    if (opacity === 0) {
      eNode.element.style.visibility = "hidden";
      eNode.element.style.pointerEvents = "none";
    } else {
      eNode.element.style.visibility = "visible";
      if (eNode._eventTypes?.size && eNode.element.style.pointerEvents === "none") {
        eNode.element.style.pointerEvents = "auto";
      }
    }
  }

  setBorderWidth(eNode) {
    const width = eNode.props["border.width"];
    const tag = eNode.element.nodeName;

    if (tag === "IMG" || tag === "VIDEO" || tag === "CANVAS") {
      eNode.element.style.outline = `${width}px solid ${eNode.props["border.color"] || "transparent"}`;
      eNode.element.style.outlineOffset = `-${width}px`;
    } else {
      if (!document.getElementById("dmaf-border-style")) {
        const style = document.createElement("style");
        style.id = "dmaf-border-style";
        style.textContent = `.dmaf-border::after { content: ""; position: absolute; inset: 0; border-style: solid; border-width: var(--border-width); border-color: var(--border-color, transparent); border-radius: var(--border-radius, 0); pointer-events: none; }`;
        document.head.appendChild(style);
      }
      eNode.element.style.setProperty("--border-width", `${width}px`);
      eNode.element.classList.add("dmaf-border");
    }
  }

  handleTransform(eNode) {
    const config = eNode.props["transform"];
    if (!config) return;

    const descriptor = descriptors.get(eNode.namespace);
    const allTransforms = config.transforms || [config];

    // Separate color transforms from numeric transforms
    const colorTransforms = [];
    const numericTransforms = [];
    for (const t of allTransforms) {
      const propType = descriptor?.properties[t.property]?.type;
      if (propType === "color") {
        colorTransforms.push(t);
      } else {
        numericTransforms.push(t);
      }
    }

    // Run color transforms via element.animate()
    for (const t of colorTransforms) {
      this._animateColor(eNode, t);
    }

    // Run numeric transforms via VisualTransformEngine
    if (numericTransforms.length === 0) return;

    const hasOpacity = numericTransforms.some(t => t.property === "appearance.opacity");
    if (hasOpacity) {
      eNode.element.style.visibility = "visible";
      if (eNode._eventTypes?.size) {
        eNode.element.style.pointerEvents = "auto";
      }
    }

    // Resolve valueStart from current eNode props where not provided
    for (const t of numericTransforms) {
      if (t.valueStart === undefined) {
        t.valueStart = eNode.props[t.property];
      }
      // Resolve "auto" for size properties
      if (t.valueEnd === "auto") {
        if (t.property === "size.width") t.valueEnd = eNode.element.scrollWidth;
        else if (t.property === "size.height") t.valueEnd = eNode.element.scrollHeight;
      }
    }

    const numericConfig = numericTransforms.length === 1 ? numericTransforms[0] : { transforms: numericTransforms };

    const applyFn = (property, value) => {
      eNode.props[property] = value;
      this._applyPropertyHandler(eNode, property);
    };

    const completeFn = () => {
      if (hasOpacity && eNode.props["appearance.opacity"] === 0) {
        eNode.element.style.visibility = "hidden";
        if (eNode._eventTypes?.size) {
          eNode.element.style.pointerEvents = "none";
        }
      }
    };

    visualTransformEngine.start(eNode.uid, numericConfig, applyFn, completeFn, completeFn);
  }

  _animateColor(eNode, t) {
    const cssProperty = COLOR_CSS_MAP[t.property];
    if (!cssProperty) return;

    const from = t.valueStart || eNode.props[t.property] || "transparent";
    const to = t.valueEnd;
    const duration = t.duration || 0;
    const delay = t.delay || 0;

    const animation = eNode.element.animate(
      [{ [cssProperty]: from }, { [cssProperty]: to }],
      { duration, delay, fill: "forwards", easing: "ease-in-out" }
    );

    animation.finished.then(() => {
      animation.cancel();
      eNode.props[t.property] = to;
      this._applyPropertyHandler(eNode, t.property);
    });
  }

  _applyPropertyHandler(eNode, property) {
    if (property === "size.width" || property === "size.height") {
      this.applyLayout(eNode);
      return;
    }
    const entry = this.handlers[property];
    if (!entry) return;
    const handler = typeof entry === "function" ? entry : entry.handler;
    if (handler) handler.call(this, eNode);
  }

  // Convention methods — called automatically by EntityEnvironment fallback
  // when the property key matches the method name (dot removed, camelCase).

  positionZIndex(eNode) {
    eNode.element.style["z-index"] = eNode.props["position.zIndex"];
  }

  interactionTouchAction(eNode) {
    eNode.element.style.touchAction = eNode.props["interaction.touchAction"];
  }

  appearanceBlendMode(eNode) {
    eNode.element.style["mix-blend-mode"] = eNode.props["appearance.blendMode"];
  }

  appearanceCursor(eNode) {
    eNode.element.style["cursor"] = eNode.props["appearance.cursor"];
  }

  appearanceBackground(eNode) {
    eNode.element.style["backgroundColor"] = eNode.props["appearance.background"];
  }

  appearanceBackdropBlur(eNode) {
    const value = eNode.props["appearance.backdropBlur"];
    if (value === undefined || value === 0) {
      eNode.element.style.backdropFilter = "";
      eNode.element.style.webkitBackdropFilter = "";
    } else {
      eNode.element.style.backdropFilter = `blur(${value}px)`;
      eNode.element.style.webkitBackdropFilter = `blur(${value}px)`;
    }
  }

  borderRadius(eNode) {
    const radius = `${eNode.props["border.radius"]}px`;
    eNode.element.style.borderRadius = radius;
    eNode.element.style.setProperty("--border-radius", radius);
  }

  borderColor(eNode) {
    const tag = eNode.element.nodeName;
    if (tag === "IMG" || tag === "VIDEO" || tag === "CANVAS") {
      const width = eNode.props["border.width"] || 0;
      eNode.element.style.outline = `${width}px solid ${eNode.props["border.color"] || "transparent"}`;
    } else {
      eNode.element.style.setProperty("--border-color", eNode.props["border.color"]);
    }
  }

  flexGrow(eNode) {
    const value = eNode.props["flex.grow"];
    if (value === undefined) return;
    eNode.element.style.flexGrow = value;
  }

  flexShrink(eNode) {
    const value = eNode.props["flex.shrink"];
    if (value === undefined) return;
    eNode.element.style.flexShrink = value;
  }

  flexBasis(eNode) {
    const value = eNode.props["flex.basis"];
    if (value === undefined) return;
    if (value === -1) {
      eNode.element.style.flexBasis = "auto";
    } else {
      eNode.element.style.flexBasis = `${value}px`;
    }
  }

  flexAlignSelf(eNode) {
    const value = eNode.props["flex.alignSelf"];
    if (value === undefined) return;
    eNode.element.style.alignSelf = value;
  }

  flexOrder(eNode) {
    const value = eNode.props["flex.order"];
    if (value === undefined) return;
    eNode.element.style.order = value;
  }

  marginTop(eNode) {
    const value = eNode.props["margin.top"];
    if (value === undefined) return;
    eNode.element.style.marginTop = cssValue(value);
  }

  marginRight(eNode) {
    const value = eNode.props["margin.right"];
    if (value === undefined) return;
    eNode.element.style.marginRight = cssValue(value);
  }

  marginBottom(eNode) {
    const value = eNode.props["margin.bottom"];
    if (value === undefined) return;
    eNode.element.style.marginBottom = cssValue(value);
  }

  marginLeft(eNode) {
    const value = eNode.props["margin.left"];
    if (value === undefined) return;
    eNode.element.style.marginLeft = cssValue(value);
  }

  sizeWidthMax(eNode) {
    const maxWidth = eNode.props["size.widthMax"];
    if (maxWidth === undefined || maxWidth === Infinity) {
      eNode.element.style.maxWidth = "none";
      return;
    }
    eNode.element.style.maxWidth = `${maxWidth}px`;
  }

  sizeHeightMax(eNode) {
    const maxHeight = eNode.props["size.heightMax"];
    if (maxHeight === undefined || maxHeight === Infinity) {
      eNode.element.style.maxHeight = "none";
      return;
    }
    eNode.element.style.maxHeight = `${maxHeight}px`;
  }

  sizeWidthMin(eNode) {
    const minWidth = eNode.props["size.widthMin"];
    if (minWidth === undefined || minWidth === 0) {
      eNode.element.style.minWidth = "0";
      return;
    }
    eNode.element.style.minWidth = `${minWidth}px`;
  }

  sizeHeightMin(eNode) {
    const minHeight = eNode.props["size.heightMin"];
    if (minHeight === undefined || minHeight === 0) {
      eNode.element.style.minHeight = "0";
      return;
    }
    eNode.element.style.minHeight = `${minHeight}px`;
  }

  filterUid(eNode) {
    eNode.element.style["filter"] += `url("#${eNode.props["filterUid"]}")`;
  }

  focus(eNode) {
    focusManager.setFocused([eNode.uid]);
  }

  _mouseButtons = ["left", "middle", "right"];
  _intersectionObserver = null;
  _currentHashId = null;

  _dispatchMessage(eNode, eventType, event = null, customProperties = {}) {
    let scaledProperties = {};
    const extraProperties = {};

    if (eventType === "down" || eventType === "up" || eventType === "dragstart") {
      extraProperties.button = this._mouseButtons[event.button || 0];
    }
    if (event && (eNode._stopPropagation?.[eventType] ?? true)) {
      event.stopPropagation();
    }

    if (
      eNode.pointersData?.gestureStarted ||
      (eNode.pointersData?.activePointers.length > 0 && eventType === "down")
    ) {
      return;
    }

    if (event) {
      scaledProperties = this._scaleProperties(event);
    }

    bridge.update({
      uid: eNode.uid,
      key: eventType,
      value: {
        id: eNode.props["id"],
        ...scaledProperties,
        ...customProperties,
        ...extraProperties
      },
      sender: "browser",
    });
  }

  _scaleProperties(event) {
    return {
      offsetX: event.currentTarget === document ? -1 : event.offsetX,
      offsetY: event.currentTarget === document ? -1 : event.offsetY,
      x: event.currentTarget === document ? -1 : event.offsetX,
      y: event.currentTarget === document ? -1 : event.offsetY,
      pageX: event.pageX,
      pageY: event.pageY,
    };
  }

  _dispatchScrollEvent(eNode, userEvent, event) {
    if (event && !event.isTrusted) return;

    bridge.update({
      uid: eNode.uid,
      key: userEvent,
      value: {
        id: eNode.props["id"],
        scrollX: event.target.scrollLeft,
        scrollY: event.target.scrollTop,
      },
      sender: "browser",
    });
  }

  _setupSmoothScroll(eNode, userEvent) {
    if ("onwheel" in document) {
      eNode.element.addEventListener("wheel", this._dispatchWheelEvent.bind(this, eNode, userEvent), { passive: true });
    }
    eNode.element.addEventListener("touchstart", this._dispatchTouchStartEvent.bind(this, eNode, userEvent));
    eNode.element.addEventListener("touchmove", this._dispatchTouchMoveEvent.bind(this, eNode, userEvent), { passive: false });
  }

  _dispatchWheelEvent(eNode, userEvent, event) {
    let { deltaX, deltaY, deltaMode } = event;
    if (deltaMode === 1) {
      deltaX *= 16;
      deltaY *= 16;
    } else if (deltaMode === 2) {
      deltaX *= window.innerWidth;
      deltaY *= window.innerHeight;
    }
    bridge.update({
      uid: eNode.uid,
      key: userEvent,
      value: {
        id: eNode.props["id"],
        scrollType: "wheel",
        deltaX: deltaX + Math.random() * 0.001,
        deltaY: deltaY + Math.random() * 0.001,
      },
      sender: "browser",
    });
  }

  _dispatchTouchStartEvent(eNode, userEvent, event) {
    const touch = event.touches[0];
    bridge.update({
      uid: eNode.uid,
      key: userEvent,
      value: {
        id: eNode.props["id"],
        scrollType: "touchStart",
        touchStartX: touch.pageX,
        touchStartY: touch.pageY,
      },
      sender: "browser",
    });
  }

  _dispatchTouchMoveEvent(eNode, userEvent, event) {
    event.preventDefault();
    const touch = event.touches[0];
    bridge.update({
      uid: eNode.uid,
      key: userEvent,
      value: {
        id: eNode.props["id"],
        scrollType: "touchMove",
        touchX: touch.pageX,
        touchY: touch.pageY,
      },
      sender: "browser",
    });
  }

  _setupDragEvent(eNode) {
    eNode.dragData = {};
    const isMobile = domEnvironment.touch;
    const downEvent = isMobile ? "touchstart" : "mousedown";
    const moveEvent = isMobile ? "touchmove" : "mousemove";
    const upEvent = isMobile ? "touchend" : "mouseup";

    eNode.dragData.downHandler = (event) => {
      const normalizedEvent = normalizeTouchEvent(event);
      eNode.dragData.originalPageX = normalizedEvent.pageX;
      eNode.dragData.originalPageY = normalizedEvent.pageY;
      this._dispatchMessage(eNode, "dragstart", normalizedEvent);

      eNode.dragData.moveHandler = (event) => {
        const normalizedEvent = normalizeTouchEvent(event);
        const pageX = normalizedEvent.pageX;
        const pageY = normalizedEvent.pageY;
        this._dispatchMessage(eNode, "dragmove", normalizedEvent, {
          deltaX: pageX - eNode.dragData.originalPageX,
          deltaY: pageY - eNode.dragData.originalPageY,
        });
        eNode.dragData.originalPageX = pageX;
        eNode.dragData.originalPageY = pageY;
        event.preventDefault();
      };

      eNode.dragData.upHandler = (event) => {
        this._dispatchMessage(eNode, "dragend", event);
        document.removeEventListener(moveEvent, eNode.dragData.moveHandler);
        document.removeEventListener(upEvent, eNode.dragData.upHandler);
        eNode.dragData.moveHandler = null;
        eNode.dragData.upHandler = null;
        this._dispatchDropEvent(eNode, event);
      };
      document.addEventListener(moveEvent, eNode.dragData.moveHandler, { passive: false });
      document.addEventListener(upEvent, eNode.dragData.upHandler);
    };
    eNode.element.addEventListener(downEvent, eNode.dragData.downHandler);
  }

  _dispatchDropEvent(eNode, event) {
    const normalizedEvent = normalizeTouchEvent(event);
    const elements = document.elementsFromPoint(normalizedEvent.clientX, normalizedEvent.clientY);
    const element = elements.find((element) => element.hasAttribute("droppable"));

    if (element) {
      const dropEvent = new MouseEvent("drop", {
        clientX: normalizedEvent.clientX,
        clientY: normalizedEvent.clientY,
      });
      dropEvent.dragTarget = eNode.uid;
      element.dispatchEvent(dropEvent);
    }
  }

  _setupDropEvent(eNode) {
    eNode.element.setAttribute("droppable", true);
    eNode.dropData = {};
    eNode.dropData.dropHandler = (event) => {
      this._dispatchMessage(eNode, "drop", event, {
        dragTarget: event.dragTarget,
        offsetX: event.offsetX,
        offsetY: event.offsetY,
      });
    };
    eNode.element.addEventListener("drop", eNode.dropData.dropHandler);
  }

  _setupGestureEvent(eNode) {
    eNode.pointersData = {
      activePointers: [],
      gestureStarted: false,
    };

    const moveListener = (event) => {
      const scaledChangedProperties = this._scaleProperties(event);
      const changedPointerIndex = eNode.pointersData.activePointers.findIndex(
        ({ pointerId }) => pointerId == event.pointerId
      );

      if (changedPointerIndex < 2) {
        const changedPointer = eNode.pointersData.activePointers[changedPointerIndex];
        const otherPointer = eNode.pointersData.activePointers[1 - changedPointerIndex];

        const prevVectorX = changedPointer.prevX - otherPointer.prevX;
        const prevVectorY = changedPointer.prevY - otherPointer.prevY;
        const currVectorX = scaledChangedProperties.x - otherPointer.prevX;
        const currVectorY = scaledChangedProperties.y - otherPointer.prevY;

        const prevCenterX = (changedPointer.prevX + otherPointer.prevX) / 2;
        const prevCenterY = (changedPointer.prevY + otherPointer.prevY) / 2;
        const currCenterX = (scaledChangedProperties.x + otherPointer.prevX) / 2;
        const currCenterY = (scaledChangedProperties.y + otherPointer.prevY) / 2;

        const prevDistance = Math.sqrt(Math.pow(prevVectorX, 2) + Math.pow(prevVectorY, 2));
        const currDistance = Math.sqrt(Math.pow(currVectorX, 2) + Math.pow(currVectorY, 2));

        const distanceDelta = currDistance - prevDistance;
        const initialDistance = Math.sqrt(Math.pow(changedPointer.initialX - otherPointer.initialX, 2) + Math.pow(changedPointer.initialY - otherPointer.initialY, 2));

        const dotProduct = (prevVectorX * currVectorX) + (prevVectorY * currVectorY);
        const wedgeProduct = (prevVectorX * currVectorY) - (prevVectorY * currVectorX);
        const angleDelta = 360 / (Math.PI) * Math.atan2(dotProduct, wedgeProduct) - 180;

        bridge.update({
          uid: eNode.uid,
          key: "gesture:change",
          value: {
            id: eNode.props["id"],
            deltaX: currCenterX - prevCenterX,
            deltaY: currCenterY - prevCenterY,
            distanceDelta,
            initialDistance,
            angleDelta,
          },
          sender: "browser",
        });
      }

      eNode.pointersData.activePointers = eNode.pointersData.activePointers.map(
        (pointer) => {
          if (pointer.pointerId !== event.pointerId) return pointer;
          return {
            ...pointer,
            prevX: scaledChangedProperties.x,
            prevY: scaledChangedProperties.y,
            prevPageX: scaledChangedProperties.pageX,
            prevPageY: scaledChangedProperties.pageY,
            prevOffsetX: scaledChangedProperties.offsetX,
            prevOffsetY: scaledChangedProperties.offsetY
          };
        }
      );
    };

    eNode.element.addEventListener("pointerup", (event) => {
      eNode.pointersData.activePointers = eNode.pointersData.activePointers.filter(
        ({ pointerId }) => pointerId !== event.pointerId
      );

      if (eNode.pointersData.activePointers.length === 0 && eNode.pointersData.gestureStarted) {
        eNode.pointersData.gestureStarted = false;
        bridge.update({
          uid: eNode.uid,
          key: "gesture:end",
          value: { id: eNode.props["id"] },
          sender: "browser",
        });
        eNode.element.removeEventListener("pointermove", moveListener);
      }
    });

    eNode.element.addEventListener("pointerdown", (event) => {
      const scaledProperties = this._scaleProperties(event);
      eNode.pointersData.activePointers.push({
        pointerId: event.pointerId,
        initialX: scaledProperties.x,
        initialY: scaledProperties.y,
        initialPageX: scaledProperties.pageX,
        initialPageY: scaledProperties.pageY,
        initialOffsetX: scaledProperties.offsetX,
        initialOffsetY: scaledProperties.offsetY,
        prevX: scaledProperties.x,
        prevY: scaledProperties.y,
      });

      if (eNode.pointersData.activePointers.length >= 2 && !eNode.pointersData.gestureStarted) {
        eNode.pointersData.gestureStarted = true;
        eNode.element.addEventListener("pointermove", moveListener);

        const pointer1 = eNode.pointersData.activePointers[0];
        bridge.update({
          uid: eNode.uid,
          key: "gesture:start",
          value: {
            id: eNode.props["id"],
            initialX: pointer1.x,
            initialY: pointer1.y,
            initialPageX: pointer1.pageX,
            initialPageY: pointer1.pageY,
            initialOffsetX: pointer1.offsetX,
            initialOffsetY: pointer1.offsetY,
          },
          sender: "browser",
        });
      }
    });
  }

  _observeIntersection(eNode) {
    if (!this._intersectionObserver) {
      this._intersectionObserver = new IntersectionObserver(this._intersectionListener.bind(this), {
        root: null,
        threshold: [0, 0.01, 1],
      });
    }
    eNode.intersectionData = { rafId: null, previousValues: {} };
    this._intersectionObserver.observe(eNode.element);
  }

  _intersectionListener(intersectionEntries) {
    for (const entry of intersectionEntries) {
      const eNode = visualNodes.get(entry.target.id);
      if (!eNode) continue;

      if (entry.intersectionRatio > 0) {
        if (!eNode.intersectionData.rafId) {
          this._startIntersectionRAF(eNode);
        }
      } else {
        if (eNode.intersectionData.rafId) {
          cancelAnimationFrame(eNode.intersectionData.rafId);
          eNode.intersectionData.rafId = null;
        }
      }
    }
  }

  _startIntersectionRAF(eNode) {
    const checkBoundingRect = () => {
      const boundingRect = eNode.element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const currentValues = {
        top: boundingRect.top,
        left: boundingRect.left,
        bottom: boundingRect.bottom,
        right: boundingRect.right,
        width: boundingRect.width,
        height: boundingRect.height,
        viewportHeight,
        viewportWidth,
      };

      const hasChanged = Object.keys(currentValues).some(
        key => currentValues[key] !== eNode.intersectionData.previousValues[key]
      );

      if (hasChanged) {
        eNode.intersectionData.previousValues = currentValues;

        const messageData = {
          ...currentValues,
          intersectionRatio: boundingRect.height > 0
            ? Math.max(0, Math.min(1, (viewportHeight - Math.max(0, boundingRect.top)) / boundingRect.height))
            : 0,
        };

        const hashId = eNode.props["hashId"];
        if (hashId !== undefined) {
          messageData.hashId = hashId;
          messageData.absoluteY = eNode.element.offsetTop;
        }

        this._dispatchMessage(eNode, "intersection", null, messageData);
      }

      const hashId = eNode.props["hashId"];
      const viewportCenter = viewportHeight / 2;
      if (hashId !== undefined && boundingRect.top < viewportCenter && boundingRect.bottom > viewportCenter) {
        if (this._currentHashId !== hashId) {
          this._currentHashId = hashId;
          if (hashId === '') {
            history.replaceState(null, '', window.location.pathname + window.location.search);
          } else {
            history.replaceState(null, '', '#' + hashId);
          }
        }
      }

      eNode.intersectionData.rafId = requestAnimationFrame(checkBoundingRect);
    };

    eNode.intersectionData.rafId = requestAnimationFrame(checkBoundingRect);
  }

  _setElementCursor(eNode) {
    if (eNode.element.style.cursor) return;

    const clickEvents = new Set(["up", "down", "click", "double_click"]);
    const isClickableEvent = [...eNode._eventTypes].some((eventType) => clickEvents.has(eventType));
    eNode.element.style.cursor = isClickableEvent ? "pointer" : "default";
  }

  _removeUserEvents(eNode) {
    if (!eNode._eventTypes) return;

    if (eNode._eventTypes.has("intersection")) {
      if (eNode.intersectionData?.rafId) {
        cancelAnimationFrame(eNode.intersectionData.rafId);
        eNode.intersectionData.rafId = null;
      }
      this._intersectionObserver?.unobserve(eNode.element);
    }

    if (eNode._eventTypes.has("focus")) {
      focusManager.removeFocusableNode(eNode.uid);
    }

    if (eNode.dragData) {
      const isMobile = domEnvironment.touch;
      const downEvent = isMobile ? "touchstart" : "mousedown";
      const moveEvent = isMobile ? "touchmove" : "mousemove";
      const upEvent = isMobile ? "touchend" : "mouseup";
      eNode.element.removeEventListener(downEvent, eNode.dragData.downHandler);
      if (eNode.dragData.moveHandler) document.removeEventListener(moveEvent, eNode.dragData.moveHandler);
      if (eNode.dragData.upHandler) document.removeEventListener(upEvent, eNode.dragData.upHandler);
    }

    if (eNode.dropData) {
      eNode.element.removeEventListener("drop", eNode.dropData.dropHandler);
    }
  }

  destroy(eNode) {
    visualTransformEngine.cancelAll(eNode.uid);
    this._removeUserEvents(eNode);
  }
}

const visualSuperHtml = new VisualSuperHtml("visual");
visualEntities.set("visual", visualSuperHtml);
export { flushInitialResizes, VisualSuperHtml, visualSuperHtml };
